import { useCallback, useContext, useMemo } from 'react';
import { useTranslations } from 'next-intl';
import { useUnit } from 'effector-react';
import classNames from 'classnames';
import { Button, ButtonSize, ButtonType } from '@nx-frontend/shared/ui/button';
import { Icon, IconsMap } from '@nx-frontend/shared/ui/icon';
import { generalEnv } from '@nx-frontend/shared/env';
import { BillingPlanCode, PaywallPlan, PlanFeatureItemWithLoc } from '@nx-frontend/shared/types/paywall-types';
import { trackTrialEndPopupSelectPlan, trackTrialStartPopupSelectPlan, trackUpgradePopupSelectPlan } from './analytics';
import { PaywallContext } from './paywall-context';
import { mapMode } from './paywall-map-mode';
import './paywall-plan-column.css';
import { $paywallStore } from './store';

interface PaywallPlanColumnProps extends PaywallPlan {
  code: BillingPlanCode;
  title: string;
  price: number;
  extraMemberPrice: number;
  features?: string[];
  whiteLabel?: boolean;
  disabled?: boolean;
  buttonText?: string;
  showExtraMemberPrice?: boolean;
  selected?: boolean;
  isMostPopular?: boolean;
  isRecommended?: boolean;
  isSelectable?: boolean;
  onClick: (plan: PaywallPlan) => void;
}
export const PaywallPlanColumn = (props: PaywallPlanColumnProps) => {
  const {
    code,
    title,
    price,
    extraMemberPrice,
    features,
    featuresLocalized,
    whiteLabel,
    disabled,
    buttonText,
    showExtraMemberPrice = true,
    isMostPopular,
    isRecommended,
    selected,
    isSelectable = true,
  } = props;

  const paywallStore = useUnit($paywallStore);
  const { showOrderSummary, mode, paywallAdditionPaymentParams, isAppsumo, reason, isFreePlan, action, getTimeDelta } =
    useContext(PaywallContext);
  const t = useTranslations();

  const discount = useMemo(() => {
    if (isAppsumo) {
      return 60;
    }
    if (action && action.discount) {
      return action.discount;
    }
    return 0;
  }, [isAppsumo, action]);

  const styles = {
    borderColor: disabled ? 'text-neutral-200' : isMostPopular ? '' : 'border-neutral-200',
    bgColor: disabled ? 'bg-zinc-50' : 'bg-white',
    textColor: disabled ? 'text-[#001216]' : 'text-neutral-800',
    textPriceColor: disabled ? 'text-[#001216]' : discount ? 'text-rose-500 font-semibold' : 'text-neutral-800',
    textDiscountOriginalPriceColor: disabled ? 'text-neutral-400' : 'text-neutral-500',
    textUsdSize: discount ? 'text-2xl' : 'text-[32px]',
    perMonthColor: disabled ? 'text-[#001216]' : 'text-neutral-500',
    perExtraMemberBgColor: disabled ? 'bg-neutral-100' : 'bg-gray-100',
    borderColorHover: !disabled && showOrderSummary && isSelectable ? 'hover:border-cyan-700' : '',
  };

  const handleClick = useCallback(() => {
    if (mode === 'upgrade' && !isFreePlan && !isAppsumo && code !== 'unlimited') {
      const { onClick, ...plan } = props;
      onClick(plan);
      trackUpgradePopupSelectPlan(code, reason, getTimeDelta());
    }
  }, [code, getTimeDelta, mode, props, isFreePlan, isAppsumo, reason]);

  const handleContactUsClick = useCallback(() => {
    window.open(`https://nimbusweb.me/book-a-demo/?ws=webnotes-next_${mapMode[mode]}`, '_blank');
  }, [mode]);

  const onPlanConfirm = useCallback(() => {
    const params = Object.keys(paywallAdditionPaymentParams).reduce((str, key) => {
      return `${str}&${key}=${paywallAdditionPaymentParams[key]}`;
    }, '');
    const trial = mode === 'trialStart' ? `&trial=1` : '';
    switch (mode) {
      case 'trialStart':
        trackTrialStartPopupSelectPlan(code);
        break;
      case 'trialEnd':
        trackTrialEndPopupSelectPlan(code, getTimeDelta());
        break;
      case 'upgrade':
        trackUpgradePopupSelectPlan(code, reason, getTimeDelta());
        break;
    }

    if (code === 'unlimited') {
      handleContactUsClick();
      return;
    }

    window.open(
      `${generalEnv.businessBillingUrl}?orgId=${paywallStore.settings.orgId}&p=${code.replace(
        /-monthly|-yearly/g,
        ''
      )}&plan=${code}` +
        trial +
        (params ?? ''),
      '_self'
    );
  }, [
    code,
    getTimeDelta,
    mode,
    paywallAdditionPaymentParams,
    paywallStore.settings.orgId,
    reason,
    handleContactUsClick,
  ]);

  return (
    <div className="paywall__plan-column relative flex flex-1 flex-col">
      {isMostPopular && !showOrderSummary ? (
        <div className="absolute h-[28px] w-full -top-[4px] rounded-[46px] left-0 bg-gradient-to-r from-[rgb(15,118,110)] to-[rgb(20,184,166)]"></div>
      ) : null}
      {isRecommended && !showOrderSummary ? (
        <div className="absolute h-[28px] w-full -top-[4px] rounded-[46px] left-0 bg-gradient-to-r from-[rgb(15,118,110)] to-[rgb(20,184,166)]"></div>
      ) : null}
      <div
        onClick={handleClick}
        className={classNames(
          'relative border rounded-[10px] flex-1 p-4 pb-8 duration-200',
          styles.bgColor,
          styles.borderColor,
          styles.borderColorHover,
          (isMostPopular && !showOrderSummary) || (isRecommended && !showOrderSummary)
            ? 'paywall__plan-column_most-popular'
            : '',
          {
            'cursor-pointer': isSelectable && showOrderSummary,
            'pointer-events-none opacity-[0.64]': disabled,
            '!border-cyan-700 shadow-[0_0_0_3px_#E0F2FE]': !disabled && selected,
            'paywall__plan-column_disabled': disabled,
          }
        )}
        data-cy-code={code}
      >
        {isMostPopular && !showOrderSummary && (
          <PaywallPlanColumnLabel title="Most popular" className="absolute top-6 right-4" />
        )}
        {isRecommended && !showOrderSummary && (
          <PaywallPlanColumnLabel title="Recommended" className="absolute top-6 right-4" />
        )}
        <div className={classNames('text-xl font-semibold px-2 pt-1', styles.textColor)}>{title}</div>

        <div className="mt-4 px-2">
          {code !== 'unlimited' ? (
            <>
              <span className={classNames('font-semibold', styles.textUsdSize, styles.textPriceColor)}>$</span>
              <span className={classNames('paywall__plan-price text-[32px] font-semibold', styles.textPriceColor)}>
                {!discount ? price : (price * (1 - discount / 100)).toFixed(1)}
              </span>
              {discount ? (
                <span
                  className={classNames(
                    'text-[32px] font-normal inline-block mx-1 line-through',
                    styles.textDiscountOriginalPriceColor
                  )}
                >
                  ${price}
                </span>
              ) : null}
              <span className={classNames('text-xs', styles.perMonthColor)}>/{t('features.paywall.per_month')}</span>
            </>
          ) : (
            <span className={classNames('paywall__plan-price text-[32px] font-semibold', styles.textPriceColor)}>
              Custom
            </span>
          )}
        </div>

        {showExtraMemberPrice ? (
          <div
            className={classNames('mt-1', {
              invisible: code === 'unlimited',
              hidden: code === 'unlimited' && showOrderSummary,
            })}
          >
            <span
              className={classNames(
                'text-xs p-1 inline-block px-2 rounded',
                styles.textColor,
                styles.perExtraMemberBgColor
              )}
            >
              {t('features.paywall.price_per_extra_member', {
                price: !isAppsumo ? extraMemberPrice : (extraMemberPrice * (1 - discount / 100)).toFixed(1),
              })}
            </span>
          </div>
        ) : null}
        {buttonText ? (
          <div
            className={classNames('max-w-[100%]', {
              'mt-8': !showOrderSummary,
              'mt-3': showOrderSummary,
            })}
          >
            <Button
              type={code === 'unlimited' ? ButtonType.STROKED : ButtonType.PRIMARY}
              size={ButtonSize.XL2}
              onClick={onPlanConfirm}
              className={classNames('paywall-plan__button', 'w-full h-9 !text-[13px] !rounded duration-200', {
                'border-neutral-200 !text-neutral-400 !bg-zinc-100': disabled,
                //'!text-white': !disabled && code !== 'unlimited',
                //'!text-cyan-900': !disabled && code === 'unlimited',
              })}
            >
              {buttonText}
            </Button>
          </div>
        ) : null}
        <div className="pl-[5px]">
          <div
            className={classNames('font-semibold text-base mb-3', styles.textColor, {
              'mt-[30px]': !!buttonText,
              'mt-2': !buttonText && !(showOrderSummary && code !== 'unlimited'),
              'mt-10': showOrderSummary && code !== 'unlimited',
            })}
          >
            {t('features.paywall.what_you_get')}:
          </div>
          <ul className={classNames(styles.textColor, 'text-sm')}>
            {features
              ? features.map((feature, i) => (
                  <li key={i} className="flex items-start gap-2 mb-3.5">
                    <Icon icon={IconsMap.CHECK_BULLET} className="w-[16px] mt-[3px]" />
                    {feature}
                  </li>
                ))
              : featuresLocalized &&
                featuresLocalized.map((feature, i) => (
                  <li key={i} className="flex items-start gap-2 mb-3.5">
                    <Icon icon={IconsMap.CHECK_BULLET} className="w-[16px] mt-[3px]" />
                    <div>
                      <PaywallFeatureLocalized feature={feature} onContactUsClick={handleContactUsClick} />
                    </div>
                  </li>
                ))}
            {whiteLabel ? (
              <li className="flex items-start gap-2">
                <Icon icon={IconsMap.CHECK_BULLET} className="w-[16px] mt-[3px]" />
                <span
                  className={classNames(
                    'font-medium',
                    !disabled
                      ? 'bg-gradient-to-r from-[#0F766E] to-[#14B8A6] bg-clip-text text-transparent'
                      : styles.textColor
                  )}
                >
                  White label
                </span>
              </li>
            ) : null}
          </ul>
        </div>
      </div>
    </div>
  );
};

interface PaywallPlanColumnLabelProps {
  title: string;
  className?: string;
}
const PaywallPlanColumnLabel = (props: PaywallPlanColumnLabelProps) => {
  const { title, className } = props;
  const { action } = useContext(PaywallContext);
  const styles = {
    bgColor: action ? 'bg-white' : 'bg-gradient-to-r from-[rgb(15,118,110)] to-[rgb(20,184,166)]',
    textColor: action ? 'text-gray-800' : 'text-white',
    borderColor: action ? 'border border-gray-800' : 'border-transparent',
  };
  return (
    <div
      className={classNames(
        'rounded-2xl inline-flex text-xs px-3 h-[27px] font-medium items-center justify-center',
        styles.bgColor,
        styles.borderColor,
        styles.textColor,
        className
      )}
    >
      {title}
    </div>
  );
};

const PaywallFeatureLocalized = ({
  feature,
  onContactUsClick,
}: {
  feature: PlanFeatureItemWithLoc;
  onContactUsClick: () => void;
}) => {
  const t = useTranslations();

  const text = useMemo(() => {
    const lockKeys = typeof feature.locKey === 'string' ? [feature.locKey] : feature.locKey;
    return lockKeys.map((item) => {
      if (!t.has(item)) return item;
      return t.rich(item, {
        ...feature.props,
        num:
          typeof feature.props.num === 'string'
            ? feature.props.num
                .replace(/_unlimited_/g, t('features.paywall.unlimited'))
                .replace(/_days_/g, t('features.paywall.days'))
            : feature.props.num,
        strong: (children) => <strong className="font-medium">{children}</strong>,
        a: (children) => (
          <span className="cursor-pointer text-cyan-900" onClick={onContactUsClick}>
            {children}
          </span>
        ),
      });
    });
  }, [feature, t, onContactUsClick]);

  return text;
};
